import '../../stylesheets/dashboards/dashboards.css'
import '../../stylesheets/dashboards/leaderboard.css'
import { useState, useEffect, useRef }                          from 'react'
import { useOutletContext }                                     from 'react-router-dom'
import { useLeaderboardAPI }                                    from '../../hooks/external/proprietary/useLeaderboardAPI.js'
import Main                                                     from './leaderboard/Main.js'
import ChartsSkeleton                                           from '../skeletons/ChartsSkeleton.js';
import GridSkeleton                                             from '../skeletons/GridSkeleton.js'

const Leaderboard = () => {
    // **====== Hooks ======** //
    const { getCosPricesForCategory, getIndicesPrices } = useLeaderboardAPI();    

    // **======= State =======** //
    // -- Lifted React State - Read Only --
    const {
        compsCategories,
        selectedCompsCategoryId,
        selectedCompanyIds,
        compsIsLoading
    } = useOutletContext()

    // -- Local React State --
    /* Data */    
    const [cosPrices, setCosPrices]                       = useState(null)
    const [indsPrices, setIndsPrices]                     = useState(null) 
    /* Gates */
    const [coPricesIsLoading, setCoPricesIsLoading]     = useState(true)       // envision the other for the agg sector data? 
    const [indPricesIsLoading, setIndPricesIsLoading]   = useState(true)        // envision one for indices prices

    // ** Local / On Demand Data Fetch For Main Feature Node Local Consumption ** //    
    /* Why Both Fetches Guard on compsIsLoading (the LCD across ) --
        1. the following two are set in same rendor so can use either of these to wait on until we fire the cos for cats and/or the cos prices for cats fetch, because they both need to wait for selectedCompsCategoryId to know which cos they are fetching for from the tables! We pick compsCategories as our anchor in the guards / dep arrays out of the two because it has a bit better semantics "wait for the compsCategories to load before we make this API call"
                setCompsCategories(response.data)
                setSelectedCompsCategoryId(response.data[0].id)
        2. and then downstream from there, the application (i.e. both the Public Comps Component and the Leaderboard component) need to wait for the selectedCompanyIds to resolve (i.e. for the presenter of the leaderboard main feature node), where both of the below are set on the same render once that data fetch is resolved **and when the initial selected company ids from that new cat are set** - and drilled down / shared with the other components, so therefore can use either of the below as our anchor in the guards / dep arrays when we need to wait for the selectedCompanyIds to resolve (i.e. render the presnters that need them because they consume them) - and we chose compsIsLoading out of the two because it has a bit better semantics "wait for the data fetch for the comps to finish loading before we make these API calls"
                setSelectedCompanyIds(initialCosSelected)
                setCompsIsLoading(false)
        3. Leadrboard main feature node shell: So therefore to make things a bit easier to read an follow - we use the LCD technique in the leaderboard main feature node shell - i.e. both dep arrays / guards anchored on compsIsLoading
            - where even though technically we only need to wait for compsCategories before we let co prices for cat fan out -> since compsIsLoading has to wait for that anyway -> and we have to wait to mount / render the presenter before compsIsLoading resolves (i.e. because selectedCompanyIds resolves in the same render and the presenter needs it / consumes it)
            - we just use compsIsLoading as our single source of truth (LCD) / anchor in the leaderboard main feature node boundary that is there to fetch all the data we need / consumed locally by this single main feature node
    */
    useEffect(() => {
        const fetchCosPricesForCategory = async () => {
            // ** Main Feature Node Boundary Loading Gate (Drilled From Higher Order Component) **
            // Acts as a guard for the presenter the way it bubbles up, i.e. need coPricesIsLoading || indPricesIsLoading for presenter 
            // and cant get them to false unless this API call runs, which wont run until we get a resolved compsIsLoading
            if (compsIsLoading) return
            try {
                const response = await getCosPricesForCategory({ selectedSectorId: selectedCompsCategoryId }) // note to flow updated name through backend so we can use object key/value pair shorthand syntax
                setCosPrices(response.data)
                console.log("[SUCCESS]: returned cos prices for category for leaderboard component", response.data.length)     
                setCoPricesIsLoading(false)
            } catch (error) {
                // logs response as undefined on network errors where didnt reach backend! i.e. no response = no response prop on the error object = no data prop on response object and no status code on response object because data and status codes are only on the response prop on the error object
                console.error("✗ [ERROR] loading company's prices:", error.response ? error.response.data.message : error.message)
                console.error('✗ [STATUS]:', error.response?.status)
                // if (error.response?.status === 402) setIsPaymentRequired(true) // not metered yet so dont need to throw modal
            }
        }
        fetchCosPricesForCategory()
    }, [compsIsLoading])      

    useEffect(() => {
        const fetchIndicesPrices = async () => {
            // ** Main Feature Node Boundary Loading Gate (Drilled From Higher Order Component) **
            // Acts as a guard for the presenter the way it bubbles up, i.e. need coPricesIsLoading || indPricesIsLoading for presenter 
            // and cant get them to false unless this API call runs, which wont run until we get a resolved compsIsLoading
            if (compsIsLoading) return
            try {
                const response = await getIndicesPrices() 
                setIndsPrices(response.data)
                console.log("[SUCCESS]: returned indices prices for leaderboard component", response.data.length)     
                setIndPricesIsLoading(false)
            } catch (error) {
                // logs response as undefined on network errors where didnt reach backend! i.e. no response = no response prop on the error object = no data prop on response object and no status code on response object because data and status codes are only on the response prop on the error object
                console.error("✗ [ERROR] loading indice's prices:", error.response ? error.response.data.message : error.message)
                console.error('✗ [STATUS]:', error.response?.status)
                // if (error.response?.status === 402) setIsPaymentRequired(true) // not metered yet so dont need to throw modal
            }
        }
        fetchIndicesPrices()
    }, [compsIsLoading]) 

    // **====== Logic ======** //    

    // ** Local Loading Gates ** //        
    if (coPricesIsLoading || indPricesIsLoading) return <GridSkeleton />
 
    return (
        <Main 
            cosPrices                  ={cosPrices}
            indsPrices                 ={indsPrices}
            compsCategories            ={compsCategories}
            selectedCompsCategoryId    ={selectedCompsCategoryId}
            selectedCompanyIds         ={selectedCompanyIds}
        />
    )
}

export default Leaderboard;